import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Nav, Spinner } from 'react-bootstrap';
import api, { mediaUrl } from '../../api/axios';
import './ScannerDataCenter.css';

const fields = [
  ['detectedName', 'detected_name', 'name', 'Nombre'],
  ['detectedTcg', 'detected_tcg', 'tcg', 'Juego / TCG'],
  ['detectedSet', 'detected_set', 'setName', 'Set / codigo'],
  ['detectedNumber', 'detected_number', 'collectorNumber', 'Numero'],
  ['detectedRarity', 'detected_rarity', 'rarity', 'Rareza'],
  ['detectedCardType', 'detected_card_type', 'cardType', 'Tipo de carta'],
  ['detectedEnergyType', 'detected_energy_type', 'energyType', 'Tipo elemental'],
  ['detectedLanguage', 'detected_language', 'language', 'Idioma']
];
const CARD_TYPE_OPTIONS = {
  pokemon: [
    'Pokemon Basico', 'Pokemon Fase 1', 'Pokemon Fase 2',
    'Pokemon ex', 'Pokemon V', 'Pokemon VMAX', 'Pokemon VSTAR',
    'Entrenador - Objeto', 'Entrenador - Partidario', 'Entrenador - Estadio',
    'Herramienta Pokemon', 'Energia Basica', 'Energia Especial'
  ],
  magic: ['Criatura', 'Artefacto', 'Encantamiento', 'Instantaneo', 'Conjuro', 'Planeswalker', 'Tierra'],
  one_piece: ['Leader', 'Character', 'Event', 'Stage', 'Don!!'],
  lorcana: ['Character', 'Action', 'Item', 'Location'],
  yugioh: ['Monstruo', 'Magia', 'Trampa']
};

const cardTypeOptions = gameSlug => CARD_TYPE_OPTIONS[String(gameSlug || '').toLowerCase()] || [];
const ENERGY_TYPE_OPTIONS = ['Incoloro', 'Planta', 'Fuego', 'Agua', 'Rayo', 'Psiquico', 'Lucha', 'Oscuridad', 'Metalico', 'Dragon', 'No aplica'];

const money = value => (Number(value || 0) / 1000000).toLocaleString('es-ES', { style: 'currency', currency: 'USD', minimumFractionDigits: 4 });
const date = value => value ? new Date(value).toLocaleString('es-ES') : '-';
const errorText = error => error?.response?.data?.error || 'No se pudo completar la accion. Vuelve a intentarlo.';

export default function ScannerDataCenter({ data, onRefresh }) {
  const [view, setView] = useState('queue');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [game, setGame] = useState('');
  const [page, setPage] = useState(1);
  const [version, setVersion] = useState(0);
  const [result, setResult] = useState({ items: [], games: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [noticeKind, setNoticeKind] = useState('success');
  const [selected, setSelected] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [form, setForm] = useState({});
  const [confirmAnalysis, setConfirmAnalysis] = useState(false);
  const [scanCost, setScanCost] = useState(null);
  const [catalogOptions, setCatalogOptions] = useState({ sets: [], rarities: [] });
  const [catalogLinks, setCatalogLinks] = useState({});
  const scan = audit?.scan;
  const proposal = scan?.ai_proposal?.recognition;

  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search); setPage(1); setSelected([]); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (view === 'stats') return;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    const catalogValues = view === 'catalog-values';
    api.get(catalogValues ? '/admin/card-scanner/catalog-values' : '/admin/card-scanner/records', {
      params: catalogValues ? undefined : { view, q: query, game, page, limit: 20 },
      signal: controller.signal
    }).then(({ data: next }) => {
      if (!controller.signal.aborted) setResult(catalogValues
        ? { items: next || [], games: [], total: next?.length || 0, pages: 1, page: 1 }
        : next);
    })
      .catch(requestError => { if (!controller.signal.aborted) setError(errorText(requestError)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [view, query, game, page, version]);

  useEffect(() => {
    const gameSlug = form.detectedTcg || 'pokemon';
    api.get('/cards/catalog-options', { params: { gameSlug } })
      .then(({ data: options }) => setCatalogOptions({ sets: options.sets || [], rarities: options.rarities || [] }))
      .catch(() => setCatalogOptions({ sets: [], rarities: [] }));
  }, [form.detectedTcg]);

  useEffect(() => {
    if (!autoRefresh || audit || auditLoading || busy || confirmAnalysis || selected.length || view === 'stats') return;
    const timer = setInterval(() => { if (!document.hidden) setVersion(value => value + 1); }, 30000);
    return () => clearInterval(timer);
  }, [autoRefresh, audit, auditLoading, busy, confirmAnalysis, selected.length, view]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(''), 7000);
    return () => clearTimeout(timer);
  }, [notice]);

  const refresh = () => { setVersion(value => value + 1); onRefresh(); };
  const changeView = key => { setView(key); setPage(1); setSelected([]); setError(''); };
  const openReview = async id => {
    setAudit({ scan: { id } });
    setAuditLoading(true);
    setAuditError('');
    try {
      const { data: loaded } = await api.get(`/admin/card-scanner/scans/${id}/audit`);
      setAudit(loaded);
      setForm({ ...Object.fromEntries(fields.map(([key, column]) => [key, loaded.scan[column] || ''])), notes: '' });
    } catch (requestError) { setAuditError(errorText(requestError)); }
    finally { setAuditLoading(false); }
  };
  const save = async action => {
    setBusy(true); setAuditError('');
    try {
      await api.patch(`/admin/card-scanner/scans/${scan.id}`, { ...form, action, revision: scan.review_revision });
      setAudit(null); setSelected([]);
      setNoticeKind('success');
      setNotice(action === 'approve' ? 'Carta aprobada y guardada en el catalogo.' : action === 'reject' ? 'Propuesta descartada.' : 'Borrador guardado, pendiente de aprobacion.');
      refresh();
    } catch (requestError) { setAuditError(errorText(requestError)); }
    finally { setBusy(false); }
  };
  const approveCatalogValue = async (item, existingSetId = null) => {
    setBusy(true); setError('');
    try {
      await api.patch(`/admin/card-scanner/catalog-values/${item.id}/approve`, {
        displayName: item.proposed_value,
        code: item.value_type === 'set' && !existingSetId ? item.proposed_value : undefined,
        existingSetId: existingSetId || undefined
      });
      setNoticeKind('success');
      setNotice(item.value_type === 'set' ? 'Set aprobado como catalogo en revision. Completa sus cartas y publicalo antes de ofrecerlo como binder prearmado.' : 'Rareza aprobada y disponible en el wizard.');
      setVersion(value => value + 1);
    } catch (requestError) { setError(errorText(requestError)); }
    finally { setBusy(false); }
  };

  const prepareAnalysis = async () => {
    setBusy(true); setError('');
    try {
      const { data: settings } = await api.get('/admin/card-scanner/settings');
      setScanCost(settings.estimatedScanCostMicros ?? null);
      setConfirmAnalysis(true);
    } catch (requestError) { setError(errorText(requestError)); }
    finally { setBusy(false); }
  };
  const reanalyze = async () => {
    setBusy(true); setError('');
    try {
      const { data: report } = await api.post('/admin/card-scanner/scans/reanalyze', { scanIds: selected });
      const failures = report.results.filter(item => item.status !== 'reanalyzed');
      setNoticeKind(failures.length ? 'warning' : 'success');
      setNotice(`${report.updated} propuesta(s) listas para revisar.${failures.length ? ` ${failures.length} sin propuesta: ${failures.map(item => item.reason === 'missing_upload_file' ? 'foto no disponible' : item.reason === 'missing_image_url' ? 'sin foto' : 'fallo de analisis').join(', ')}.` : ''}`);
      setSelected([]); setConfirmAnalysis(false); setVersion(value => value + 1); onRefresh();
    } catch (requestError) { setConfirmAnalysis(false); setError(errorText(requestError)); }
    finally { setBusy(false); }
  };

  const renderReviewControl = key => {
    const common = {
      value: form[key] || '',
      disabled: busy,
      onChange: event => setForm(current => ({ ...current, [key]: event.target.value }))
    };
    if (key === 'detectedSet') {
      const currentKnown = catalogOptions.sets.some(set => set.name === form[key] || set.code === form[key]);
      return <Form.Select {...common}>
        <option value=''>Set no identificado</option>
        {form[key] && !currentKnown && <option value={form[key]}>Propuesta: {form[key]}</option>}
        {catalogOptions.sets.map(set => <option key={set.id} value={set.name}>{set.name}{set.code ? ' (' + set.code + ')' : ''}</option>)}
      </Form.Select>;
    }
    if (key === 'detectedRarity') {
      const currentKnown = catalogOptions.rarities.some(rarity => rarity.display_name === form[key]);
      return <Form.Select {...common}>
        <option value=''>Rareza no identificada</option>
        {form[key] && !currentKnown && <option value={form[key]}>Propuesta: {form[key]}</option>}
        {catalogOptions.rarities.map(rarity => <option key={rarity.id || rarity.code} value={rarity.display_name}>{rarity.display_name}</option>)}
      </Form.Select>;
    }
    if (key === 'detectedCardType') {
      const options = cardTypeOptions(form.detectedTcg);
      const currentKnown = options.includes(form[key]);
      return <Form.Select {...common}>
        <option value=''>Tipo no identificado</option>
        {form[key] && !currentKnown && <option value={form[key]}>Valor guardado: {form[key]}</option>}
        {options.map(option => <option key={option} value={option}>{option}</option>)}
      </Form.Select>;
    }
    if (key === 'detectedEnergyType') {
      return <Form.Select {...common}>
        <option value=''>No identificado</option>
        {form[key] && !ENERGY_TYPE_OPTIONS.includes(form[key]) && <option value={form[key]}>Valor guardado: {form[key]}</option>}
        {ENERGY_TYPE_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
      </Form.Select>;
    }
    if (key === 'detectedLanguage') {
      return <Form.Select {...common}>
        <option value='unknown'>No identificado</option>
        <option value='es'>Español</option>
        <option value='en'>Inglés</option>
        <option value='jp'>Japonés</option>
        <option value='other'>Otro</option>
      </Form.Select>;
    }
    return <Form.Control maxLength={250} {...common} />;
  };

  return <section className='dc-workspace'>
    <header className='dc-heading'>
      <div><h2>Data Center</h2><span className='text-muted'>Catalogo y revision de cartas</span></div>
      <div className='dc-actions'>
        <Form.Check type='switch' id='dc-auto' label='Actualizar automaticamente' checked={autoRefresh} onChange={event => setAutoRefresh(event.target.checked)} />
        <Button variant='outline-dark' onClick={refresh} disabled={busy} aria-label='Actualizar Data Center' title='Actualizar'><i className='bi bi-arrow-clockwise' /></Button>
      </div>
    </header>
    <Nav variant='tabs' activeKey={view} onSelect={changeView}>
      {[['queue', 'Por revisar'], ['catalog-values', 'Sets y rarezas'], ['catalog', 'Catalogo verificado'], ['history', 'Historial'], ['stats', 'Uso y costes']].map(([key, label]) => <Nav.Item key={key}><Nav.Link eventKey={key}>{label}</Nav.Link></Nav.Item>)}
    </Nav>
    {notice && <Alert variant={noticeKind} role='status' className='mt-3' dismissible onClose={() => setNotice('')}>{notice}</Alert>}
    {error && <Alert variant='danger' className='mt-3'>{error}</Alert>}
    {view === 'stats' ? <>
      <p className='text-muted mt-3'>Todos los juegos · acumulado</p>
      {!data ? <Alert variant='warning'>No se pudieron cargar las estadisticas.</Alert> : <>
        <div className='dc-metrics'>
          {[['Analisis', data.summary?.total_scans], ['Ultimos 7 dias', data.summary?.scans_last_7_days], ['Coste estimado', money(data.summary?.estimated_cost_micros)], ['Cartas en memoria', data.memory?.learned_cards]].map(([label, value]) => <div key={label}><span>{label}</span><strong>{value || 0}</strong></div>)}
        </div>
        <h3 className='mt-4'>Actividad por set</h3>
        {(data.bySet || []).map(item => <div className='dc-stat-row' key={item.label}><span>{item.label}</span><strong>{item.total} analisis</strong></div>)}
        <h3 className='mt-4'>Fuentes de analisis</h3>
        {(data.bySource || []).map(item => <div className='dc-stat-row' key={item.source}><span>{item.source}</span><span>{item.total} · {money(item.estimated_cost_micros)}</span></div>)}
      </>}
    </> : <>
      <div className='dc-toolbar'>
        <Form.Control aria-label='Buscar cartas' type='search' placeholder='Nombre, set o numero' value={search} onChange={event => setSearch(event.target.value)} />
        <Form.Select aria-label='Filtrar por juego' value={game} onChange={event => { setGame(event.target.value); setPage(1); setSelected([]); }}>
          <option value=''>Todos los TCG</option>
          {result.games.map(item => <option key={item} value={item}>{item}</option>)}
        </Form.Select>
        {!['catalog', 'catalog-values'].includes(view) && <Button variant='outline-dark' disabled={!selected.length || busy || loading} onClick={prepareAnalysis}><i className='bi bi-stars me-2' />Reanalizar ({selected.length}/10)</Button>}
      </div>
      <div className='dc-result-count' role='status'>{loading ? 'Cargando...' : `${result.total} ${view === 'queue' ? 'pendientes' : 'resultados'}`}</div>
      {!loading && !error && !result.items.length && <p className='dc-empty'>{view === 'queue' ? 'No hay cartas pendientes con estos filtros.' : 'No hay resultados con estos filtros.'}</p>}
      <div className='dc-records' aria-busy={loading}>
        {!error && result.items.map(item => <article className='dc-record' key={item.id}>
          {!['catalog', 'catalog-values'].includes(view) && <Form.Check aria-label={`Seleccionar ${item.detected_name || 'analisis'}`} checked={selected.includes(item.id)} disabled={busy || loading || (!selected.includes(item.id) && selected.length >= 10)} onChange={() => setSelected(current => current.includes(item.id) ? current.filter(id => id !== item.id) : [...current, item.id])} />}
          {view === 'catalog-values'
            ? <div className='dc-thumb dc-no-photo'><i className={`bi ${item.value_type === 'set' ? 'bi-collection' : 'bi-stars'}`} /></div>
            : item.image_url ? <img className='dc-thumb' src={mediaUrl(item.image_url)} alt={item.detected_name || 'Carta escaneada'} loading='lazy' /> : <div className='dc-thumb dc-no-photo'><i className='bi bi-card-image' aria-label='Sin foto' /></div>}
          <div className='dc-record-copy'>
            <strong>{view === 'catalog-values' ? item.proposed_value : item.display_name || item.detected_name || 'Sin nombre'}</strong>
            <span>{view === 'catalog-values' ? `${item.game_slug} - ${item.value_type === 'set' ? 'Set' : 'Rareza'}` : [item.game_slug || item.detected_tcg, item.set_name || item.detected_set, item.collector_number || item.detected_number].filter(Boolean).join(' - ') || 'Datos incompletos'}</span>
            <small>{view === 'catalog-values' ? `${item.occurrences} deteccion(es) - ${item.proposed_by_name || 'Usuario'}` : [item.rarity || item.detected_rarity, item.language || item.detected_language].filter(Boolean).join(' - ')}</small>
            <small>{date(item.updated_at || item.created_at)}</small>
          </div>
          <div className='dc-record-actions'>
            <Badge bg={view === 'catalog' || item.reviewed_at ? 'success' : 'light'} text={view === 'catalog' || item.reviewed_at ? undefined : 'dark'}>{view === 'catalog-values' ? 'Pendiente' : view === 'catalog' ? 'Verificada' : item.reviewed_at ? 'Revisada' : item.ai_proposal ? 'Propuesta IA' : item.status === 'ignored' ? 'Descartada' : 'Pendiente'}</Badge>
            {view === 'catalog-values'
              ? item.value_type === 'set' ? <><Form.Select size='sm' aria-label={`Vincular ${item.proposed_value} a un set`} value={catalogLinks[item.id] || ''} disabled={busy || loading} onChange={event => setCatalogLinks(current => ({ ...current, [item.id]: event.target.value }))}><option value=''>Crear set nuevo</option>{catalogOptions.sets.map(set => <option key={set.id} value={set.id}>{set.name}{set.code ? ' (' + set.code + ')' : ''} � {set.language || '-'}</option>)}</Form.Select><Button className='btn-cardora' size='sm' disabled={busy || loading} onClick={() => approveCatalogValue(item, catalogLinks[item.id] || null)}><i className={`bi ${catalogLinks[item.id] ? 'bi-link-45deg' : 'bi-plus-circle'} me-1`} />{catalogLinks[item.id] ? 'Vincular alias' : 'Crear set'}</Button></> : <Button className='btn-cardora' size='sm' disabled={busy || loading} onClick={() => approveCatalogValue(item)}><i className='bi bi-check2 me-1' />Aprobar</Button>
              : <Button variant='outline-dark' size='sm' disabled={busy || loading || !(item.last_scan_id || view !== 'catalog')} onClick={() => openReview(view === 'catalog' ? item.last_scan_id : item.id)}><i className='bi bi-eye me-1' />Revisar</Button>}
          </div>        </article>)}
      </div>
      <footer className='dc-pagination'>
        <Button variant='outline-dark' aria-label='Pagina anterior' disabled={loading || result.page <= 1} onClick={() => { setPage(result.page - 1); setSelected([]); }}><i className='bi bi-chevron-left' /></Button>
        <span>Pagina {result.page} de {result.pages}</span>
        <Button variant='outline-dark' aria-label='Pagina siguiente' disabled={loading || result.page >= result.pages} onClick={() => { setPage(result.page + 1); setSelected([]); }}><i className='bi bi-chevron-right' /></Button>
      </footer>
    </>}

    <Modal show={Boolean(audit)} onHide={() => { if (!busy && !auditLoading) setAudit(null); }} size='xl' centered scrollable>
      <Modal.Header><Modal.Title>Revision de carta</Modal.Title></Modal.Header>
      <Modal.Body>
        {auditError && <Alert variant='danger'>{auditError}</Alert>}
        {auditLoading ? <Spinner animation='border' role='status'><span className='visually-hidden'>Cargando</span></Spinner> : scan?.review_revision !== undefined && <>
          <div className='dc-review'>
            <div className='dc-photo'>{scan.image_url ? <img src={mediaUrl(scan.image_url)} alt='Foto original de la carta' /> : <p>Sin foto disponible</p>}
              <span>{scan.reviewed_at ? `Ultima aprobacion: ${date(scan.reviewed_at)}` : 'Pendiente de aprobacion'}</span>
              {proposal && <span>Propuesta: {scan.ai_proposal.provider} · {scan.ai_proposal.model} · {proposal.confidence ?? '-'}% confianza estimada</span>}
            </div>
            <div>
              {proposal && <Button className='mb-3' variant='outline-dark' disabled={busy} onClick={() => setForm(current => ({ ...current, ...Object.fromEntries(fields.filter(([, , key]) => proposal[key] != null).map(([key, , aiKey]) => [key, String(proposal[aiKey])])) }))}><i className='bi bi-arrow-down-circle me-2' />Usar propuesta IA</Button>}
              <div className='dc-fields'>
                {fields.map(([key, column, aiKey, label]) => <Form.Group key={key} controlId={`dc-${key}`}>
                  <Form.Label>{label}</Form.Label>
                  {renderReviewControl(key)}
                  {proposal?.[aiKey] != null && <div className='dc-suggestion'><span>IA: {String(proposal[aiKey])}</span><Button variant='link' size='sm' title={`Usar ${label.toLowerCase()} propuesto`} aria-label={`Usar ${label.toLowerCase()} propuesto`} disabled={busy} onClick={() => setForm(current => ({ ...current, [key]: String(proposal[aiKey]) }))}><i className='bi bi-arrow-up-circle' /></Button></div>}
                  {scan[column] && form[key] !== scan[column] && <small className='text-muted'>Guardado: {scan[column]}</small>}
                </Form.Group>)}
              </div>
              <Form.Group className='mt-3' controlId='dc-notes'><Form.Label>Nota de revision</Form.Label><Form.Control as='textarea' rows={2} maxLength={2000} value={form.notes || ''} disabled={busy} onChange={event => setForm(current => ({ ...current, notes: event.target.value }))} /></Form.Group>
            </div>
          </div>
          <details className='dc-history'><summary>Historial de cambios ({audit.corrections?.length || 0})</summary>
            {(audit.corrections || []).map(change => <div className='dc-history-entry' key={change.id}>
              <strong>{change.correction_source} · {change.corrected_by_name || 'Sistema'}</strong><small>{date(change.created_at)}</small>
              {fields.map(([key, , aiKey, label]) => {
                const next = change.corrected_values?.recognition?.[aiKey] ?? change.corrected_values?.[key] ?? change.corrected_values?.[aiKey];
                const previous = change.previous_values?.[key];
                return next !== undefined && next !== previous ? <div key={key}><b>{label}: </b>{previous || '-'} → {String(next ?? '-')}</div> : null;
              })}
              {change.notes && <p>{change.notes}</p>}
            </div>)}
          </details>
        </>}
      </Modal.Body>
      <Modal.Footer className='dc-review-actions'>
        <Button variant='outline-dark' disabled={busy || auditLoading} onClick={() => setAudit(null)}>Cerrar</Button>
        {scan?.review_revision !== undefined && !auditLoading && <>
          <Button variant='outline-danger' disabled={busy} onClick={() => save('reject')}>Descartar propuesta</Button>
          <Button variant='outline-dark' disabled={busy} onClick={() => save('save')}>Guardar borrador</Button>
          <Button className='btn-cardora' disabled={busy || !form.detectedName?.trim() || !form.detectedTcg?.trim() || !form.detectedSet?.trim() || !form.detectedNumber?.trim()} onClick={() => save('approve')}>{busy ? 'Guardando...' : 'Aprobar carta'}</Button>
        </>}
      </Modal.Footer>
    </Modal>
    <Modal show={confirmAnalysis} onHide={() => { if (!busy) setConfirmAnalysis(false); }} centered>
      <Modal.Header><Modal.Title>Reanalizar {selected.length} cartas</Modal.Title></Modal.Header>
      <Modal.Body><p>Las propuestas quedaran pendientes de revision. Los datos aprobados se conservan.</p><p>Coste estimado: <strong>{scanCost == null ? 'No disponible' : money(scanCost * selected.length)}</strong></p></Modal.Body>
      <Modal.Footer><Button variant='outline-dark' disabled={busy} onClick={() => setConfirmAnalysis(false)}>Cancelar</Button><Button className='btn-cardora' disabled={busy} onClick={reanalyze}>{busy ? 'Analizando...' : 'Confirmar reanalisis'}</Button></Modal.Footer>
    </Modal>
  </section>;
}
